import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  Discount,
  Warning,
} from '@mui/icons-material';
import { CouponPreview } from "../../../components/couponPreview/CouponPreview";
import { availableTags } from "../publish-property/CampaignPage/CampaignPage.helpers";
import { PropertyCouponService } from "../../../services/PropertyCouponService";
import { CreatePropertyCouponDTO, UpdatePropertyCouponDTO } from "../../../types/PropertyCoupon";
import { PropertyListing } from "../../../types/Property";

interface PropertyCouponPageProps {
    property: PropertyListing;
    onCouponUpdate?: () => void;
}

export const PropertyCouponPage: React.FC<PropertyCouponPageProps> = ({ 
  property, 
  onCouponUpdate 
}) => {
  const [createCoupon, setCreateCoupon] = useState<CreatePropertyCouponDTO>({ 
    title: '', 
    selectedTag: 'tag_red', 
    propertyId: property.id_Property 
  });
  const [updateCoupon, setUpdateCoupon] = useState<UpdatePropertyCouponDTO>({ 
    title: property.coupon?.title || '', 
    selectedTag: property.coupon?.selectedTag?.replace('/assets/images/', '').replace('.png', '') || 'tag_red'
  });
  
  // Estados para UI
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  const propertyCouponService = new PropertyCouponService();

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const getSelectedTagInfo = (tag?: string) => {
    const tagToFind = tag || (property.coupon ? 
      property.coupon.selectedTag?.replace('/assets/images/', '').replace('.png', '') : 
      createCoupon.selectedTag);
    return availableTags.find(tagItem => tagItem.value === tagToFind) || availableTags[0];
  };

  const handleDeleteCoupon = async () => {
    if (!property.coupon?.id) return;
    
    setIsSubmitting(true);
    try {
      const response = await propertyCouponService.deleteCoupon(property.coupon.id);
      if (response) {
        showSnackbar('Cupón eliminado exitosamente', 'success');
        setShowDeleteDialog(false);
        onCouponUpdate?.(); // Refrescar datos del parent
      } else {
        throw new Error('Error al eliminar el cupón');
      }
    } catch (error) {
      console.error('Error deleting coupon:', error);
      showSnackbar('Error al eliminar el cupón', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateCoupon = async () => {
    if (!property.coupon?.id || !updateCoupon.title.trim()) {
      showSnackbar('El título es obligatorio', 'error');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const updateData = {
        title: updateCoupon.title.trim(),
        selectedTag: `/assets/images/${updateCoupon.selectedTag}.png`
      };
      
      const response = await propertyCouponService.updateCoupon(property.coupon.id, updateData);
      if (response) {
        showSnackbar('Cupón actualizado exitosamente', 'success');
        setIsEditMode(false);
        onCouponUpdate?.(); // Refrescar datos del parent
      } else {
        throw new Error('Error al actualizar el cupón');
      }
    } catch (error) {
      console.error('Error updating coupon:', error);
      showSnackbar('Error al actualizar el cupón', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateCoupon = async () => {
    if (!createCoupon.title.trim()) {
      showSnackbar('El título es obligatorio', 'error');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const createData = {
        title: createCoupon.title.trim(),
        selectedTag: `/assets/images/${createCoupon.selectedTag}.png`,
        propertyId: createCoupon.propertyId
      };
      
      const response = await propertyCouponService.createCoupon(createData);
      if (response) {
        showSnackbar('Cupón creado exitosamente', 'success');
        setCreateCoupon({ title: '', selectedTag: 'tag_red', propertyId: property.id_Property });
        onCouponUpdate?.(); // Refrescar datos del parent
      } else {
        throw new Error('Error al crear el cupón');
      }
    } catch (error) {
      console.error('Error creating coupon:', error);
      showSnackbar('Error al crear el cupón', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setUpdateCoupon({
      title: property.coupon?.title || '', 
      selectedTag: property.coupon?.selectedTag?.replace('/assets/images/', '').replace('.png', '') || 'tag_red'
    });
  };

  const formatPropertyTitle = (title: string) => {
    return title.length > 50 ? `${title.substring(0, 50)}...` : title;
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: '#f8fafc' }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <Discount sx={{ color: '#23afa4ff', fontSize: 25 }} />
          <Box>
            <Typography variant="h4" fontWeight="600" color="#333" sx={{fontSize: { xs: '1.2rem', sm: '1.5rem' }}}>
              Gestión de Cupón
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {formatPropertyTitle(property.title)}
            </Typography>
          </Box>
        </Stack>
        
        <Typography variant="body2" color="text.secondary">
          {property.coupon ? 
            'Tu propiedad tiene un cupón activo. Puedes editarlo o eliminarlo.' : 
            'Esta propiedad no tiene cupón. Crea uno para atraer más visitantes.'
          }
        </Typography>
      </Paper>

      <Grid container spacing={4}>
        {/* Panel de gestión */}
        <Grid item xs={12} md={6}>
          {property.coupon ? (
            /* Sección de editar/eliminar cupón existente */
            <Card elevation={3} sx={{ height: 'fit-content' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                  <Typography variant="h6" fontWeight="600" color="#333" sx={{mr: 2, fontSize: { xs: '0.9rem', sm: '1rem' }}}>
                    Cupón Actual
                  </Typography>
                  {!isEditMode && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => setIsEditMode(true)}
                        sx={{
                          borderColor: '#23afa4ff',
                          color: '#23afa4ff',
                          '&:hover': {
                            borderColor: '#1e8e8b',
                            backgroundColor: '#f0f9ff',
                          },
                        }}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<Delete />}
                        onClick={() => setShowDeleteDialog(true)}
                      >
                        Eliminar
                      </Button>
                    </Stack>
                  )}
                </Stack>

                <Stack spacing={3}>
                  <TextField
                    label="Título del Cupón"
                    value={isEditMode ? updateCoupon.title : property.coupon.title}
                    onChange={(e) => setUpdateCoupon({...updateCoupon, title: e.target.value})}
                    fullWidth
                    disabled={!isEditMode}
                    variant={isEditMode ? "outlined" : "filled"}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: '#23afa4ff',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#23afa4ff',
                      },
                    }}
                  />

                  <FormControl fullWidth disabled={!isEditMode}>
                    <InputLabel
                      sx={{
                        '&.Mui-focused': {
                          color: '#23afa4ff',
                        },
                      }}
                    >
                      Tipo de Cupón
                    </InputLabel>
                    <Select
                      value={isEditMode ? updateCoupon.selectedTag : 
                        property.coupon.selectedTag?.replace('/assets/images/', '').replace('.png', '') || 'tag_red'}
                      onChange={(e) => setUpdateCoupon({...updateCoupon, selectedTag: e.target.value})}
                      label="Tipo de Cupón"
                      variant={isEditMode ? "outlined" : "filled"}
                      sx={{
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#23afa4ff',
                        },
                      }}
                    >
                      {availableTags.map((tag) => (
                        <MenuItem key={tag.value} value={tag.value}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: 1,
                                backgroundColor: tag.color,
                                border: '1px solid #ddd',
                              }}
                            />
                            <Typography variant="body2">{tag.label}</Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {isEditMode && (
                    <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
                      <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={handleCancelEdit}
                        disabled={isSubmitting}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <Save />}
                        onClick={handleUpdateCoupon}
                        disabled={isSubmitting || !updateCoupon.title.trim()}
                        sx={{
                          backgroundColor: '#23afa4ff',
                          '&:hover': {
                            backgroundColor: '#1e8e8b',
                          },
                        }}
                      >
                        {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ) : (
            /* Sección de crear nuevo cupón */
            <Card elevation={3} sx={{ height: 'fit-content' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                  <Add sx={{ color: '#23afa4ff' }} />
                  <Typography variant="h6" fontWeight="600" color="#333">
                    Crear Nuevo Cupón
                  </Typography>
                </Stack>

                <Stack spacing={3}>
                  <TextField
                    label="Título del Cupón"
                    value={createCoupon.title}
                    onChange={(e) => setCreateCoupon({...createCoupon, title: e.target.value})}
                    fullWidth
                    variant="outlined"
                    placeholder="Ej: Descuento Especial 20%"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: '#23afa4ff',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#23afa4ff',
                      },
                    }}
                  />

                  <FormControl fullWidth>
                    <InputLabel
                      sx={{
                        '&.Mui-focused': {
                          color: '#23afa4ff',
                        },
                      }}
                    >
                      Tipo de Cupón
                    </InputLabel>
                    <Select
                      value={createCoupon.selectedTag}
                      onChange={(e) => setCreateCoupon({...createCoupon, selectedTag: e.target.value})}
                      label="Tipo de Cupón"
                      sx={{
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#23afa4ff',
                        },
                      }}
                    >
                      {availableTags.map((tag) => (
                        <MenuItem key={tag.value} value={tag.value}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: 1,
                                backgroundColor: tag.color,
                                border: '1px solid #ddd',
                              }}
                            />
                            <Typography variant="body2">{tag.label}</Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <Add />}
                    onClick={handleCreateCoupon}
                    disabled={isSubmitting || !createCoupon.title.trim()}
                    fullWidth
                    size="large"
                    sx={{
                      mt: 3,
                      backgroundColor: '#23afa4ff',
                      '&:hover': {
                        backgroundColor: '#1e8e8b',
                      },
                      py: 1.5,
                    }}
                  >
                    {isSubmitting ? 'Creando Cupón...' : 'Crear Cupón'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Preview del cupón */}
        <Grid item xs={12} md={6}>
          <Card elevation={3} sx={{ height: 'fit-content', position: 'sticky', top: 20 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="600" color="#333" mb={3}>
                Vista Previa del Cupón
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <CouponPreview
                  title={property.coupon ? 
                    (isEditMode ? updateCoupon.title : property.coupon.title) : 
                    createCoupon.title || 'Ejemplo de Cupón'
                  }
                  selectedTag={property.coupon ? 
                    (isEditMode ? updateCoupon.selectedTag : 
                      property.coupon.selectedTag?.replace('/assets/images/', '').replace('.png', '') || 'tag_red') : 
                    createCoupon.selectedTag
                  }
                  getSelectedTagInfo={getSelectedTagInfo}
                  isEditMode={isEditMode}
                />
              </Box>

              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ backgroundColor: '#f8fafc', p: 2, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Estado:</strong> {property.coupon ? 'Cupón Activo' : 'Sin Cupón'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  <strong>Color:</strong> {getSelectedTagInfo().label}
                </Typography>
                {property.coupon && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    <strong>Creado:</strong> {property.coupon.createdDate ? 
                      new Date(property.coupon.createdDate).toLocaleDateString() : 'N/A'}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog de confirmación para eliminar */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Warning color="error" />
            <Typography variant="h6">Confirmar Eliminación</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            ¿Estás seguro de que deseas eliminar este cupón?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Esta acción no se puede deshacer. El cupón será removido permanentemente de la propiedad.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => setShowDeleteDialog(false)}
            variant="outlined"
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteCoupon}
            variant="contained"
            color="error"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <Delete />}
          >
            {isSubmitting ? 'Eliminando...' : 'Eliminar Cupón'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};